import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { getLinkedProduct } from 'utils/storefront/get-linked-products';
import { trackPDPKeyIngredientClick } from 'utils/tracking';

const Context = createContext();

const ProductContext = ({ children, data }) => {
  // Safeguard against undefined data
  const safeData = data || {};
  const safeProduct = safeData.product || {};

  const [productData, setProductData] = useState({
    product: safeProduct,
    products: [safeProduct].filter(Boolean),
  });
  const [actualProduct, setActualProduct] = useState(safeProduct);

  // Get active variant ID from URL params
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());

  // Find initial variant based on ID or first available
  let initialVariant = {};

  if (safeData.isBundle) {
    initialVariant = safeData.variant || {};
  } else {
    const safeVariants = safeProduct.variants || [];
    initialVariant = params.variant
      ? safeVariants.find((v) => v.id === `gid://shopify/ProductVariant/${params.variant}`)
      : null;
    initialVariant = initialVariant || safeVariants.find((v) => v.availableForSale) || safeVariants[0] || {};
  }

  // Default current variant to the initial variant
  const [currentVariant, setCurrentVariant] = useState(initialVariant);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getLinkedProduct(data.group);
      setProductData(response);
    };

    if (data.group) {
      // eslint-disable-next-line no-console
      fetchData().catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (data.group) {
      const currentProduct = productData.products.find((p) => p.variants.find((v) => v.id === currentVariant.id));
      const id = currentVariant.id.split('/ProductVariant/')[1];
      const url = new URL(window.location);
      url.pathname = `${Shopify.routes.root}products/${currentProduct.handle}`;
      url.searchParams.set('variant', id);
      window.history.replaceState(null, '', url);

      setActualProduct(currentProduct);
    }
  }, [currentVariant]);

  // Details flyout state
  const [flyoutState, setFlyoutState] = useState(false);
  const [flyoutOpenElement, setflyoutOpenElement] = useState(null);
  const openFlyout = (e) => {
    setFlyoutState(true);
    setflyoutOpenElement(e.target);
    trackPDPKeyIngredientClick(e.target.innerText);
  };
  const closeFlyout = () => setFlyoutState(false);

  return data.isBundle ? (
    <Context.Provider
      value={{
        ...data,
        actualProduct,
        freeShipping: {
          freeShippingText: data.product.freeShippingText.value,
          standardShipping: data.product.standardShipping.value,
        },
        detailsFlyout: {
          flyoutState,
          openFlyout,
          closeFlyout,
          flyoutOpenElement,
          flyoutTitle: data.detailsFlyout.title,
          productIcons: data.detailsFlyout.productIcons,
          flyoutContentMiddle: data.detailsFlyout.contentMiddle,
          commitmentTitle: data.detailsFlyout.commitmentTitle,
          commitmentIcons: data.detailsFlyout.commitmentIcons,
          flyoutContentEnd: data.detailsFlyout.contentEnd,
        },
      }}
    >
      {children}
    </Context.Provider>
  ) : (
    <Context.Provider
      value={{
        ...data,
        freeShipping: {
          freeShippingText: data.product.freeShippingText.value,
          standardShipping: data.product.standardShipping.value,
        },
        detailsFlyout: {
          flyoutState,
          openFlyout,
          closeFlyout,
          flyoutOpenElement,
          flyoutTitle: data.detailsFlyout.title,
          productIcons: data.detailsFlyout.productIcons,
          flyoutContentMiddle: data.detailsFlyout.contentMiddle,
          commitmentTitle: data.detailsFlyout.commitmentTitle,
          commitmentIcons: data.detailsFlyout.commitmentIcons,
          flyoutContentEnd: data.detailsFlyout.contentEnd,
        },
        singleBundleItems: data.product.singleBundleItems,
        currentVariant,
        setCurrentVariant,
        product: productData.product,
        actualProduct,
        maxOrderQTY: data.maxOrderQTY,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const withProductContext = (Rendered) => (props) =>
  <Context.Consumer>{(value) => <Rendered {...props} {...value} />}</Context.Consumer>;

export default ProductContext;
